import React from 'react';
import styled from 'styled-components';

import { BUTTON_TYPES } from '../../../shared/constants';
import { IPage, IVideo } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';

import { DEQ_COMPS } from '../../../utils/deq';
import { RichTextHtml } from '../../../utils/richtext/richTextRender';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';

interface Props {
  to?: IPage;
  label: string;
  body?: string;
  video?: IVideo;
  title?: string;
  subtitle?: string;
}

const VideoCTA = ({ to, label, body, video, title, subtitle }: Props) => (
  <Container>
    {(title || subtitle || body) && (
      <TextContainer>
        {title && <Title>{title}</Title>}
        {subtitle && <Description>{subtitle}</Description>}
        {body && (
          <Description>
            <RichTextHtml
              richTextNode={body}
              deqCompType={DEQ_COMPS.videoCta}
            />
          </Description>
        )}
        {to && (
          <PrimaryButton
            type={BUTTON_TYPES.internal}
            label={label}
            to={to}
            className="primary-button-videoCTA"
            deqElements={{
              type: 'button',
              componentType: 'ContentWrapper',
              label: label?.toLowerCase(),
              elementStyle: 'primary',
            }}
          />
        )}
      </TextContainer>
    )}
    {video && (
      <ImageContainer>
        <video muted playsInline autoPlay loop>
          <source src={video.file?.url} type="video/mp4" />
        </video>
      </ImageContainer>
    )}
  </Container>
);

const Container = styled.div`
  background-color: ${colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  display: flex;
  flex-direction: column-reverse;

  ${mq.from.breakpoint.L`
    flex-direction: row;
    align-items: center;
  `}
`;

const ImageContainer = styled.div`
  height: 100%;
  width: 100%;

  ${mq.from.breakpoint.L`
    display: flex;
    align-items: center;
    min-height: 50vh;
    width: 50%;
  `}

  > video {
    width: 100%;
  }
`;

const TextContainer = styled.div`
  align-items: flex-start;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px;
  width: 100%;

  ${mq.from.breakpoint.L`
    width: 50%;
    padding: 32px 48px;
  `}
`;

const Title = styled.div`
  font-size: 24px;
  font-style: normal;
  font-weight: normal;
  line-height: 32px;
  margin-bottom: 16px;

  ${mq.from.breakpoint.M`
    font-size: 32px;
    line-height: 40px;
    margin-bottom: 24px;
  `}
`;

const Description = styled.div`
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 24px;
  width: 100%;

  ${mq.from.breakpoint.M`
    margin-bottom: 40px;
  `}

  p {
    margin-top: 32px;
    margin-bottom: 0;

    &:first-of-type {
      margin-top: 0;
    }
  }

  ul {
    margin: 0;
  }
`;

export default VideoCTA;
